import type { ChangeEvent, ComponentProps } from 'react';
import React from 'react';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { Flex } from 'common/components/Flex';
import Input from 'common/components/Input';
import Icon from 'common/components/Reclamation/Icon';
import IconButton from 'common/components/Reclamation/IconButton';
import { useLocalization } from 'common/providers/LocaleProvider';

interface PartNumberFieldProps {
  value: string;
  name: string;
  id: string;
  index: number;
  isDisabled: boolean;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

const ZOOM_IN_DURATION = 300;

const zoomInOut = keyframes`
  0% {
   transform: scale(1, 1);
  }
  50% {
    transform: scale(1.02, 1.02);
  }
  100% {
    transform: scale(1, 1);
  }
`;

const UnstyledWrapper = styled(Flex)<any>`
  ${({ isFirst }) =>
    !isFirst &&
    css`
      animation: ${zoomInOut} ${ZOOM_IN_DURATION}ms;
    `}
`;

const Wrapper = (props: ComponentProps<typeof UnstyledWrapper>) => (
  <UnstyledWrapper alignItems="center" mb="sp_16" {...props} />
);

const UnstyledStyledInput = styled(Input)``;

const StyledInput = (props: ComponentProps<typeof UnstyledStyledInput>) => (
  <UnstyledStyledInput px="sp_12" mb="sp_0" {...props} />
);

const UnstyledStyledIconButton = styled(IconButton)``;

const StyledIconButton = (props: ComponentProps<typeof UnstyledStyledIconButton>) => (
  <UnstyledStyledIconButton ml="sp_4" {...props} />
);

const PartNumberField = ({
  value,
  name,
  id,
  index,
  isDisabled,
  onChange,
  onRemove,
}: PartNumberFieldProps) => {
  const { t } = useLocalization();

  const handleInputChange = ({ target: { value: val } }: ChangeEvent<HTMLInputElement>) => {
    onChange(index, val);
  };

  const handleInputRemoval = () => {
    onRemove(index);
  };

  return (
    <Wrapper {...(index > 0 ? { as: 'label' } : {})} isFirst={index === 0}>
      <StyledInput
        type="text"
        name={name}
        id={id}
        placeholder={t('h24_part_number_input_placeholder')}
        value={value}
        disabled={isDisabled}
        onChange={handleInputChange}
        autoComplete="off"
      />
      {index > 0 && (
        <StyledIconButton aria-label={t('h24_delete')} onClick={handleInputRemoval}>
          <Icon name="close" />
        </StyledIconButton>
      )}
    </Wrapper>
  );
};

export default PartNumberField;
